import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, AlertCircle } from "lucide-react";
import DeviceDetailSkeleton from "@/components/device/DeviceDetailSkeleton";
import DeviceHeader from "@/components/device/DeviceHeader";
import DeviceTabs from "@/components/device/DeviceTabs";
import { type Device, normalizeStatus } from "@/hooks/useDevices";

import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useNavigationContext } from "@/hooks/useNavigationContext";
import { Component, type ReactNode, useState, useRef } from "react";
import * as devicesApi from "@/services/devicesApi";
import { useCompactMode } from "@/hooks/useCompactMode";
import { DeviceDetailRouteBoundaryWithRouter } from "./DeviceDetailRouteBoundary";
import { getCustomerName } from "@/utils/customerLookup";

const safeText = (value: unknown): string => typeof value === "string" ? value : value == null ? "" : String(value);

class DeviceSectionBoundary extends Component<{ children: ReactNode; label: string }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error(`Device details ${this.props.label} failed to render`, error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
          This device section could not be displayed because some reported data is incomplete.
        </div>
      );
    }

    return this.props.children;
  }
}

const DeviceDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getBackNavigation } = useNavigationContext();
  const [activeTab, setActiveTab] = useState("info");
  const tabsRef = useRef<HTMLDivElement>(null);
  const { cx } = useCompactMode();
  
  const { data: device, isLoading, error } = useQuery({
    queryKey: ['device', id],
    queryFn: async () => {
      if (!id) throw new Error('Device ID is required');
      
      const [detail, summary] = await Promise.all([
        devicesApi.getDeviceDetail(id),
        devicesApi.getDeviceSummary(id).catch(() => null),
      ]);
      const wrapper = detail as any;
      // unwrap returns { device, applications, certificates }
      // Merge in flat summary fields (username/fullName/userEmail/userId/tenantName)
      // that the /data/{uuid} wrapper may omit.
      const dev = { ...(summary || {}), ...(wrapper?.device || wrapper) } as any;
      // Preserve user-identity fields from the summary endpoint when the
      // detail wrapper doesn't carry them.
      if (summary) {
        dev.username = dev.username || (summary as any).username;
        dev.fullName = dev.fullName || (summary as any).fullName;
        dev.userEmail = dev.userEmail || (summary as any).userEmail;
        dev.userId = dev.userId || (summary as any).userId;
        dev.tenantName = dev.tenantName || (summary as any).tenantName;
        dev.tenantId = dev.tenantId || (summary as any).tenantId;
      }
      const apps = wrapper?.applications || [];
      
      const epochToIso = (epoch?: number): string => {
        if (!epoch) return '';
        return new Date(epoch * 1000).toISOString();
      };

      const inferPlatform = (d: any): string => {
        const platform = safeText(d.platform || d.osType || d.os_type).toLowerCase();
        if (platform) return platform;
        const product = safeText(d.product || d.modelName).toLowerCase();
        if (product.includes('iphone') || product.includes('ipad') || product.includes('ipod')) return 'ios';
        if (product.includes('mac') || product.includes('imac')) return 'macos';
        if (product.includes('appletv')) return 'tvos';
        return '';
      };

      const installedApps = Array.isArray(apps) ? apps.map((a: any) => ({
        name: safeText(a?.name || a?.appName) || 'Unknown App',
        version: safeText(a?.version || a?.shortVersion) || null,
        bundle_id: safeText(a?.bundleIdentifier || a?.identifier),
        installed_date: safeText(a?.installed_date || a?.installedAt) || undefined,
        icon: safeText(a?.icon) || undefined,
      })) : [];

      const scriptDeployments = Array.isArray(dev.scriptDeployments || dev.script_deployments)
        ? (dev.scriptDeployments || dev.script_deployments).map((deployment: any, index: number) => ({
          id: safeText(deployment?.id) || `deployment-${index}`,
          status: safeText(deployment?.status) || 'unknown',
          last_run: safeText(deployment?.last_run || deployment?.lastRun) || null,
          exit_code: typeof deployment?.exit_code === 'number' ? deployment.exit_code : typeof deployment?.exitCode === 'number' ? deployment.exitCode : null,
          output: safeText(deployment?.output) || null,
          retry_enabled: typeof deployment?.retry_enabled === 'boolean' ? deployment.retry_enabled : typeof deployment?.retryEnabled === 'boolean' ? deployment.retryEnabled : null,
          configurations: deployment?.configurations ? { name: safeText(deployment.configurations.name) || 'Unknown Script' } : null,
        }))
        : [];

      const normalizedStatus = normalizeStatus(dev.status);
      const lastSeen = epochToIso(dev.lastConnectedAt) || dev.lastSeen || dev.last_seen || '';

      const deviceData: Device = {
        id: dev.uuid || dev.id || id,
        name: dev.name || dev.deviceName || 'Unknown Device',
        hostname: dev.hostname || dev.deviceName || '',
        os_type: inferPlatform(dev),
        os_version: dev.osVersion || dev.os || dev.os_version || '',
        status: normalizedStatus,
        last_seen: lastSeen,
        created_at: dev.createdAt || dev.created_at || '',
        serial_number: dev.serialNumber || dev.serial_number || null,
        ip_address: dev.ipAddress || dev.ip_address || dev.deviceAdditionalProperties?.WiFiMAC || null,
        battery_level: dev.deviceAdditionalProperties?.BatteryLevel ?? dev.batteryLevel ?? null,
        battery_status: dev.batteryStatus ?? dev.battery_status ?? null,
        installed_apps: installedApps,
        department: null,
        location: null,
        customer: dev.tenantName || (dev.tenantId ? getCustomerName(dev.tenantId) : null),
        model: dev.model || dev.modelName || null,
        product: dev.product || null,
        username: dev.username || null,
        fullName: dev.fullName || null,
        userEmail: dev.userEmail || null,
        userId: dev.userId || null,
        deviceAdditionalProperties: dev.deviceAdditionalProperties || undefined,
        script_deployments: scriptDeployments,
      };
      return deviceData;
    },
    enabled: !!id,
    staleTime: 25000,
    retry: 1,
  });

  const handleBackClick = () => {
    const backNav = getBackNavigation();
    navigate(backNav?.path || '/devices');
  };

  const handleCardClick = (tab: string) => {
    setActiveTab(tab);
    setTimeout(() => {
      tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <DeviceDetailSkeleton />
        </div>
      </div>
    );
  }

  if (error || !device) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="container max-w-7xl mx-auto flex flex-col items-center justify-center text-center py-16 space-y-3">
          <div className="h-14 w-14 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="h-7 w-7 text-destructive" aria-hidden="true" />
          </div>
          <p className="text-base font-semibold text-foreground">Device unavailable</p>
          <p className="text-sm text-muted-foreground max-w-xs">
            {error instanceof Error ? error.message : "We couldn't load this device's details."}
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate('/devices')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Devices
          </Button>
        </div>
      </div>
    );
  }

  
  const backNav = getBackNavigation();

  return <div className="h-full flex flex-col">
      <div className="border-b bg-card/30">
        <div className={cx("flex items-center justify-between px-6 py-4", "flex items-center justify-between px-6 py-2.5")}>
          <Button variant="ghost" size="sm" onClick={handleBackClick}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {backNav?.label || 'Back to Devices'}
          </Button>
        </div>
        <div className={cx("px-6 pb-8 pt-4", "px-6 pb-5 pt-2")}>
          <DeviceSectionBoundary label="header">
            <DeviceHeader device={device} onCardClick={handleCardClick} />
          </DeviceSectionBoundary>
        </div>
      </div>
      <div className={cx("flex-1 overflow-auto px-6 py-6", "flex-1 overflow-auto px-6 py-4")}>
        <div ref={tabsRef}>
          <DeviceSectionBoundary label="tabs">
            <DeviceTabs device={device} activeTab={activeTab} onTabChange={setActiveTab} />
          </DeviceSectionBoundary>
        </div>
      </div>
    </div>;
};

const DeviceDetailWithBoundary = () => (
  <DeviceDetailRouteBoundaryWithRouter>
    <DeviceDetail />
  </DeviceDetailRouteBoundaryWithRouter>
);

export default DeviceDetailWithBoundary;
